import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, KeyRound } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";

const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
const TOKEN_KEY = "auth_token";

function cleanBearer(t?: string | null) {
  let raw = (t || "").trim();
  if (raw.toLowerCase().startsWith("bearer ")) raw = raw.slice(7).trim();
  return raw;
}

export default function FirstLoginChangePassword() {
  const { toast } = useToast();
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPwd || newPwd.length < 6) {
      toast({ title: "Mot de passe trop court", description: "Minimum 6 caractères", variant: "destructive" });
      return;
    }
    if (newPwd !== confirmPwd) {
      toast({
        title: "Confirmation incorrecte",
        description: "Les deux mots de passe ne correspondent pas",
        variant: "destructive",
      });
      return;
    }
    setBusy(true);
    try {
      const token = cleanBearer(localStorage.getItem(TOKEN_KEY));
      const res = await fetch(`${API_BASE}/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ current_password: oldPwd, new_password: newPwd }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `Erreur ${res.status}`);

      if (data.access_token) localStorage.setItem(TOKEN_KEY, data.access_token);

      const u = data.user || {};
      login({
        email: u.email || user?.email || "",
        name: u.name || user?.name || "",
        role: u.role || user?.role || "User",
        mustChangePassword: !!u.must_change_password,
      });

      toast({ title: "Mot de passe changé ✅" });
      navigate("/dashboard");
    } catch (err: any) {
      toast({ title: "Échec", description: err?.message || "Erreur inconnue", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 overflow-hidden">
      {/* Background flou */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          backgroundImage: "ur[](https://images.unsplash.com/photo-1557682250-33bd709cbe85?auto=format&fit=crop&w=1920&q=80)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(10px)",
          transform: "scale(1.08)",
        }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-background/70 via-background/50 to-background/70" />

      {/* Carte */}
      <div className="relative w-full max-w-md animate-in fade-in-0 zoom-in-95 duration-300">
        <div className="rounded-3xl shadow-2xl backdrop-blur-xl border border-white/20 bg-white/30 dark:bg-black/30">
          <div className="p-6 sm:p-7 space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-semibold">Changer le mot de passe</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Pour des raisons de sécurité, vous devez définir un nouveau mot de passe avant d’accéder à l’application.
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="old">Ancien mot de passe</Label>
                <div className="relative">
                  <Input
                    id="old"
                    type={show1 ? "text" : "password"}
                    value={oldPwd}
                    onChange={(e) => setOldPwd(e.target.value)}
                    className="pr-9 bg-background/60"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                    onClick={() => setShow1((v) => !v)}
                    aria-label={show1 ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {show1 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new1">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input
                    id="new1"
                    type={show2 ? "text" : "password"}
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    className="pr-9 bg-background/60"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                    onClick={() => setShow2((v) => !v)}
                    aria-label={show2 ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {show2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new2">Confirmer</Label>
                <Input
                  id="new2"
                  type="password"
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)}
                  className="bg-background/60"
                />
              </div>

              <Button type="submit" className="w-full h-11" disabled={busy}>
                {busy ? "Enregistrement…" : "Valider"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
